import Phaser from 'phaser';
import { CooldownTimer } from '../game/CooldownTimer';
import {
  LEFT_PANEL_WIDTH, RIGHT_PANEL_WIDTH, TOP_PANEL_HEIGHT, BOTTOM_PANEL_HEIGHT,
  CANVAS_WIDTH, CANVAS_HEIGHT, MAZE_WIDTH, MAZE_HEIGHT, COLORS, PANEL_COLOR,
} from '../utils/constants';

export class UIScene extends Phaser.Scene {
  private playerCooldown!: CooldownTimer;
  private aiCooldown!: CooldownTimer;
  private getScore!: () => number;
  private highScores!: number[];
  private demoMode = false;
  private level = 1;
  private getLives!: () => number;
  private livesText!: Phaser.GameObjects.Text;
  private playerBar!: Phaser.GameObjects.Graphics;
  private aiBar!: Phaser.GameObjects.Graphics;
  private scoreDigits: Phaser.GameObjects.Text[] = [];
  private scoreBoxes: Phaser.GameObjects.Rectangle[] = [];
  private highScoreDigits: Phaser.GameObjects.Text[][] = [];
  private barWidth = 12;
  private barHeight = 100;

  constructor() {
    super({ key: 'UIScene' });
  }

  init(data: {
    playerCooldown: CooldownTimer;
    aiCooldown: CooldownTimer;
    getScore: () => number;
    highScores: number[];
    demoMode: boolean;
    level: number;
    getLives: () => number;
  }): void {
    this.playerCooldown = data.playerCooldown;
    this.aiCooldown = data.aiCooldown;
    this.getScore = data.getScore;
    this.highScores = data.highScores;
    this.demoMode = data.demoMode;
    this.level = data.level;
    this.getLives = data.getLives;
  }

  create(): void {
    const leftX = LEFT_PANEL_WIDTH / 2;
    const rightX = CANVAS_WIDTH - RIGHT_PANEL_WIDTH / 2;
    const scoreCenterX = LEFT_PANEL_WIDTH + MAZE_WIDTH / 2;
    const scoreCenterY = TOP_PANEL_HEIGHT / 2;

    // === Top panel ===
    this.add.rectangle(
      CANVAS_WIDTH / 2, TOP_PANEL_HEIGHT / 2,
      CANVAS_WIDTH, TOP_PANEL_HEIGHT,
      PANEL_COLOR, 1,
    );

    // === Bottom panel ===
    this.add.rectangle(
      CANVAS_WIDTH / 2, TOP_PANEL_HEIGHT + MAZE_HEIGHT + BOTTOM_PANEL_HEIGHT / 2,
      CANVAS_WIDTH, BOTTOM_PANEL_HEIGHT,
      PANEL_COLOR, 1,
    );

    // === Retro arcade score (5 digit boxes, sized to fit top panel) ===
    if (!this.demoMode) {
      const digitW = 15;
      const digitH = 19;
      const gap = 2;
      const totalW = 5 * digitW + 4 * gap;
      const startX = scoreCenterX - totalW / 2 + digitW / 2;

      this.scoreDigits = [];
      this.scoreBoxes = [];
      for (let i = 0; i < 5; i++) {
        const dx = startX + i * (digitW + gap);

        const box = this.add.rectangle(dx, scoreCenterY, digitW, digitH, 0xeeeeee, 1);
        box.setStrokeStyle(1, 0x999999);
        this.scoreBoxes.push(box);

        const digit = this.add.text(dx, scoreCenterY, '0', {
          fontSize: '13px',
          color: '#111111',
          fontStyle: 'bold',
          fontFamily: 'monospace',
        }).setOrigin(0.5);
        this.scoreDigits.push(digit);
      }

      this.add.text(startX - digitW / 2 - 6, scoreCenterY, 'SCORE', {
        fontSize: '8px',
        color: '#555555',
        fontStyle: 'bold',
      }).setOrigin(1, 0.5);

      // Level display (right-aligned to maze edge)
      const mazeRight = LEFT_PANEL_WIDTH + MAZE_WIDTH;
      this.add.text(mazeRight - 8, scoreCenterY, String(this.level), {
        fontSize: '13px',
        color: '#ffffff',
        fontStyle: 'bold',
        fontFamily: 'monospace',
      }).setOrigin(1, 0.5);

      this.add.text(mazeRight - 8 - 20, scoreCenterY, 'LVL', {
        fontSize: '8px',
        color: '#555555',
        fontStyle: 'bold',
      }).setOrigin(1, 0.5);

      // Lives display (left side of top panel)
      this.add.text(LEFT_PANEL_WIDTH + 8, scoreCenterY, 'LIVES', {
        fontSize: '8px',
        color: '#555555',
        fontStyle: 'bold',
      }).setOrigin(0, 0.5);

      this.livesText = this.add.text(LEFT_PANEL_WIDTH + 42, scoreCenterY, '♥♥♥', {
        fontSize: '12px',
        color: '#ff4444',
        fontFamily: 'monospace',
      }).setOrigin(0, 0.5);
    }

    // === Player panel (left) ===
    this.add.text(leftX, TOP_PANEL_HEIGHT + 16, 'PLAYER', {
      fontSize: '10px', color: '#00aaff', fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    this.add.text(leftX, TOP_PANEL_HEIGHT + 30, 'WALL', {
      fontSize: '9px', color: '#00aaff',
    }).setOrigin(0.5, 0);

    this.playerBar = this.add.graphics();

    // High scores (below cooldown bar) — mini retro digit boxes
    if (!this.demoMode) {
      const hsY = TOP_PANEL_HEIGHT + 190;
      this.add.text(leftX, hsY, 'BEST', {
        fontSize: '9px', color: '#666666', fontStyle: 'bold',
      }).setOrigin(0.5, 0);

      const miniDigitW = 10;
      const miniDigitH = 14;
      const miniGap = 2;
      const miniTotalW = 5 * miniDigitW + 4 * miniGap;

      this.highScoreDigits = [];
      for (let i = 0; i < 5; i++) {
        const rowY = hsY + 16 + i * (miniDigitH + 4);
        const rowStartX = leftX - miniTotalW / 2 + miniDigitW / 2;
        const rowDigits: Phaser.GameObjects.Text[] = [];

        for (let d = 0; d < 5; d++) {
          const dx = rowStartX + d * (miniDigitW + miniGap);
          this.add.rectangle(dx, rowY, miniDigitW, miniDigitH, 0xcccccc, 1)
            .setStrokeStyle(1, 0x888888);
          const digit = this.add.text(dx, rowY, '-', {
            fontSize: '10px',
            color: '#111111',
            fontStyle: 'bold',
            fontFamily: 'monospace',
          }).setOrigin(0.5);
          rowDigits.push(digit);
        }
        this.highScoreDigits.push(rowDigits);
      }
      this.refreshHighScores();
    }

    // === AI panel (right) ===
    this.add.text(rightX, TOP_PANEL_HEIGHT + 16, 'REMNANT', {
      fontSize: '10px', color: '#ff6644', fontStyle: 'bold',
    }).setOrigin(0.5, 0);

    this.add.text(rightX, TOP_PANEL_HEIGHT + 30, 'WALL', {
      fontSize: '9px', color: '#663333',
    }).setOrigin(0.5, 0);

    this.aiBar = this.add.graphics();
  }

  update(): void {
    this.drawBar(
      this.playerBar,
      LEFT_PANEL_WIDTH / 2,
      TOP_PANEL_HEIGHT + 46,
      this.playerCooldown,
      COLORS.COOLDOWN_READY,
      COLORS.COOLDOWN_ACTIVE,
    );

    this.drawBar(
      this.aiBar,
      CANVAS_WIDTH - RIGHT_PANEL_WIDTH / 2,
      TOP_PANEL_HEIGHT + 46,
      this.aiCooldown,
      COLORS.AI_COOLDOWN_READY,
      COLORS.AI_COOLDOWN_ACTIVE,
    );

    // Update score digits
    if (this.scoreDigits.length > 0 && this.getScore) {
      const score = this.getScore();
      const padded = String(Math.min(score, 99999)).padStart(5, '0');
      for (let i = 0; i < 5; i++) {
        this.scoreDigits[i].setText(padded[i]);
      }
    }

    // Update lives
    if (this.livesText && this.getLives) {
      const lives = this.getLives();
      this.livesText.setText('♥'.repeat(Math.max(0, lives)));
    }

    // Refresh high scores
    if (!this.demoMode && this.highScoreDigits.length > 0) {
      this.refreshHighScores();
    }
  }

  private refreshHighScores(): void {
    for (let i = 0; i < this.highScoreDigits.length; i++) {
      const digits = this.highScoreDigits[i];
      if (i < this.highScores.length) {
        const padded = String(Math.min(this.highScores[i], 99999)).padStart(5, '0');
        for (let d = 0; d < 5; d++) {
          digits[d].setText(padded[d]);
        }
      } else {
        for (let d = 0; d < 5; d++) {
          digits[d].setText('-');
        }
      }
    }
  }

  private drawBar(
    gfx: Phaser.GameObjects.Graphics,
    centerX: number,
    barY: number,
    timer: CooldownTimer,
    readyColor: number,
    activeColor: number,
  ): void {
    if (!timer) return;

    const barX = centerX - this.barWidth / 2;

    gfx.clear();

    gfx.fillStyle(0x333333, 0.8);
    gfx.fillRect(barX, barY, this.barWidth, this.barHeight);

    const progress = timer.progress;
    const color = timer.isReady ? readyColor : activeColor;
    const fillHeight = this.barHeight * progress;
    gfx.fillStyle(color, 1);
    gfx.fillRect(barX, barY + this.barHeight - fillHeight, this.barWidth, fillHeight);

    gfx.lineStyle(1, 0x666666, 1);
    gfx.strokeRect(barX, barY, this.barWidth, this.barHeight);
  }
}
